/**
 * GET /api/v1/account/users/search — searchable, paginated user lookup.
 *
 * Used instead of `GET /users`, which takes no search term or page and returns
 * the whole account. Also open to every user, not just org admins.
 */
import { http } from '@/lib/http';

/** Backend caps this at 100. */
export const USER_SEARCH_PAGE_SIZE = 50;

export interface SearchedUser {
  id: number;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_thumb_url?: string | null;
}

export interface FetchUserSearchArgs {
  /** Free-text term. */
  query?: string;
  items?: number;
  /**
   * Restricts to users who may be sent on behalf of — delegation enabled and a
   * connected mailbox. Sending as anyone else is rejected by the send endpoint
   * with `err_email_delegation_disabled`, after the email is composed.
   */
  emailDelegatableOnly?: boolean;
  signal?: AbortSignal;
}

export async function fetchUserSearch({
  query,
  items = USER_SEARCH_PAGE_SIZE,
  emailDelegatableOnly = false,
  signal,
}: FetchUserSearchArgs = {}): Promise<SearchedUser[]> {
  const trimmed = query?.trim();
  const res = await http.get('account/users/search', {
    params: {
      items,
      page: 1,
      // Without this, deactivated and never-invited users come back too.
      filter_by: {
        status: ['active'],
        ...(emailDelegatableOnly ? { email_delegatable: [true] } : {}),
      },
      ...(trimmed ? { filter_value: trimmed } : {}),
    },
    signal,
  });
  // Interceptor already unwrapped axios, so `res` is the JSON body.
  const users = (res as { data?: { users?: unknown } })?.data?.users;
  return Array.isArray(users) ? (users as SearchedUser[]) : [];
}

/** Name if we have one, else email — never a blank chip. */
export function searchedUserName(user: SearchedUser): string {
  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return name || user.email || `User ${user.id}`;
}
